<template>
    <div>
        <!-- <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert> -->
        
        <div>
            <slot v-bind="{ transitions, triggerByName, loading, loadingTransitions }">
                <!-- FOR REFERENCE - don't show anything here by default -->
                <!-- <b-link
                    v-for="trans in transitions"
                    :key="trans.trigger"
                    @click="triggerByName(trans.trigger)"
                >
                    {{ trans.label }} - {{ trans.description }}
                </b-link> -->
            </slot>
        </div>
        
        <b-modal
            v-model="collectDataModal"
            :title="activeTransition.label"
            hide-header
            hide-footer
            centered
            @hide="onModalHide"
            @hidden="onModalHidden"
            :size="collectDataIsOnlyConfirmation ? 'sm' : 'md'"
        >
            <FormulateForm
                v-model="collectDataValues"
                :schema="finalSchema"
                @submit="handleCollectDataSubmit"
                #default="{ isLoading }"
            >  <!-- @cancel="handleCollectDataCancel"  moved to button itself, but this works if adding button using form schema -->
                <!-- :keep-model-data="true" works fine -- but the data still gets sent!  don't want that --> 
                <div class="mt-5 d-flex justify-content-end gap-2">
                    <FormulateInput
                        type="button"
                        label="Cancel"
                        :outer-class="['minimal']"
                        @click="handleCollectDataCancel"
                        :disabled="isLoading"
                    />
                    <!-- Modal header (and therefore close button) is hidden so we must always show this -->
                    <FormulateInput
                        type="submit"
                        name="submit"
                        :label="isLoading ? 'Loading...' : collectDataSubmitLabel"
                        :disabled="isLoading"
                    />
                </div>
            </FormulateForm>
        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import { errorHandling } from '../js/custom-vue-mixins';
import capitalize from 'lodash/capitalize';
//import get from 'lodash/get';
import invoke from 'lodash/invoke';
import cloneDeep from 'lodash/cloneDeep';

// you might not want this to actually display any actions/transitions
// instead, use it mostly renderless
// use it to show the modal for collectData and to pass data to parent with events for what current transitions are?
// UPDATED - this now works with ANY state machine transitions
// as long as they follow same format as Rfx and RfxResponse state machines
// and as long as you pass in suppliosApi endpoints to use

/* HOW TO USE:
From parent, put a ref on here and pass in 
    ref="transitionComponent"
    NO LONGER :id="myObject.id" 
    NO LONGER :possible-transitions="object.possible_transitions" (Array)
    NO LONGER trigger-endpoint="rfxResponse.call_transition" (endpoint in suppliosApi that you'll call to trigger it)

then when ready to trigger it, call its method using a $ref
    (from parent) this.$refs.transitionComponent.trigger(transitionObject, endpointName('resource.action'), objectId)

this component will show modal to get collect_data or show confirmation
then call API to make the transition
when done, it will emit this.$emit('state-change', apiResp);
listen for @state-change in parent -- apiResp will be whatever you return from trigger endpoint for this object
likely will be object with { success: true, rfx: rfxObject }

*/

export default {
    name: 'StateMachineTransitions',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        fetchEndpoint: {
            type: String,
            required: false,
        },
        callEndpoint: {
            type: String,
            required: false,
        },
        objectId: {
            type: [String, Number],
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,  // true if we are loading while calling a transition
            collectDataModal: false,  // v-model that show/hide collect data modal
            // PUT DEFAULTS FOR ALL OF THESE IN resetActiveTransition
            activeTransition: null,  // OBJECT when we start calling a transition, put it here AND FREEZE IT -- then reset it when done
            collectDataValues: null,  // OBJECT v-model with the FormulateForm - if you have an activeTransition that requires collect_data, v-model to here - make sure to reset it when done
            initialSchema: null,  // ARRAY the initial schema we set once when starting a new transition... filter this down and set on finalSchema
            finalSchema: null,  // ARRAY the final schema, a filtered version of initialSchema
            collectDataSubmitLabel: null,  // STRING set this when you're setting the schema... but not putting the button in the schema -- putting it in default slot of FormulateForm so we can easily use isLoading
            collectDataIsOnlyConfirmation: false, // if ONLY showing a confirmation message, we may want to display stuff differently
            collectDataHiddenFields: null,  // should be a Set() of field names you want to hide
            stateChangeComplete: false,  // always start with this... then if its complete set to 'true' -- otherwise anytime modal is hidden (in @hide, before we reset things), we must emit 'cancel' event
            // NEW VERSION DATA below -- to trigger generic triggers from same instance
            endpointString: '',  // put something here when you call trigger()
            localObjectId: '',  // the object you're transitioning, set when you call trigger(), and make sure to reset
            // don't include this one in reset method:
            canStartTrigger: false,  // NOT calling this in resetActiveTransition -- only calling it when we know things are done (@hidden of modal) a safety mechanism to prevent starting new trigger while things are in transitional state, another transition active, etc. - reset this when we know modal is closed, etc.
            
            // FETCHING TRANSITIONS
            // this works if you do these things:
            // - set fetch-endpoint="rfx.get_transitions" or something similar
            // - set call-endpoint="rfx.call_transition" or something similar
            // - object-id="..." id of the thing being transitioned
            // - call ref.triggerByName('some_trigger') - this makes sure it uses the transitions object that it fetched itself -- the ID of object being transitioned should be 'hard-coded' as the prop, since we need that to fetch the transitions in the first place
            loadingTransitions: false,  // like loading, but for loading transitions
            isFetchMode: false,  // will set to true in created if it has enough stuff to fetch the transitions -- then will ignore the normal .trigger() method for non-fetched transitions
            transitions: [],  // list of fetched transitions that we fetch here
        }
    },
    computed: {
    },
    created() {
        // SEE IF WE HAVE ENOUGH VALUES FROM PROPS TO CONSIDER THIS A FETCH MODE
        if (this.fetchEndpoint && this.callEndpoint && this.objectId) {
            // then this is fetch mode!  we'll call fetchEndpoint to get possible transitions
            this.isFetchMode = true;
            // we don't have to set other things like localObjectId, endpoint... those get reset in resetActiveTransition depending if isFetchMode
        }
        this.resetActiveTransition();
        this.canStartTrigger = true;
    },
    async mounted() {
        if (this.isFetchMode) {
            await this.fetchTransitions();  // will set transitions in data
        }
    },
    methods: {
        // PUBLIC METHODS - if using this like a renderless component
        trigger(transitionObject, triggerEndpoint, objId) {
            // we call this internally when calling triggerByName when in isFetchMode
            // make sure everything is reset first -- then start doing things
            this.resetActiveTransition();
            // NEW version that can handle ANY trigger, from mixed types, 
            // as long as we pass in the full trigger/transition object (from possible_transitions in the caller), 
            // endpoint string in suppliosApi (like rfx.call_transition) 
            // and the ID of the thing being transitioned
            if (!this.canStartTrigger) return;  // if we can't trigger right now, just return 
            this.endpointString = triggerEndpoint;
            this.localObjectId = objId;
            this.startTransition(transitionObject);
        },
        triggerByName(triggerName) {
            // CAN ONLY USE THIS IF isFetchMode - because ID must be known in advance to fetch the transitions and stash here
            if (!this.isFetchMode) return;  // no-op if not isFetchMode
            const foundTransObj = this.transitions.find(trans => trans.trigger === triggerName);
            if (!foundTransObj) return;  // no-op if could not find the transition object
            // now we can just follow the normal flow of things as if we were NOT in localFetchMode
            this.trigger(foundTransObj, this.callEndpoint, this.objectId);
        },
        // PRIVATE METHODS
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        resetActiveTransition() {
            //console.log('HELLO FROM resetActiveTransition');
            this.activeTransition = {};
            this.collectDataValues = {};
            this.initialSchema = [];
            this.finalSchema = [];
            this.collectDataSubmitLabel = 'Continue';
            this.collectDataIsOnlyConfirmation = false;
            this.collectDataHiddenFields = new Set();  // emtpy Set as default
            this.stateChangeComplete = false;  // must do this AFTER we checked it to know if we should emit 'cancel' event
            // NEW STUFF FOR GENERIC VERSION
            this.endpointString = this.isFetchMode ? this.callEndpoint : '';  // if isFetchMode, use value from prop
            this.localObjectId = this.isFetchMode ? this.objectId : '';  // if isFetchMode, use value from prop
            this.collectDataModal = false;  // this is likely already true, but just to be sure its false...
            // This is intended to work with both isFetchMode and not
            // do not clear transitions[] in here -- that only gets done once during lifecycle of component
        },
        buildConfirmationElement(confObject) {
            // pass in the entire 'confirmation' object from collect_data
            // this will format it as needed for formulate schema and return new object
            const confDesc = confObject.description ?? '';  // might be undefined, string, or array... set each one as 'p' element in children
            const confParaList = confDesc ? (
                (Array.isArray(confDesc) && confDesc.length) ? confDesc : [ confDesc ]  // if we have something -- make sure its an array
                ) : [];  // if no description at all, just use empty array
            const pComponents = confParaList.map(para => {
                return { component: 'p', children: para };
            });
            return {
                component: 'div',
                class: 'confirmation-message mt-2 mb-4',
                children: [
                    { component: 'h4', class: 'mb-3', children: (confObject.label || '') },
                    ...pComponents,  // one 'p' component for each item in description
                    //{ component: 'p', children: (confObject.description || '') },
                ],
            };
        },
        buildFormElement(ele) {
            // pass in a SINGLE form schema object from your backend... this will format things for Vue Formulate schema
            // returns object formatted to use directly in Vue Formulate schema
            // not meant to handle your confirmation elements... separate method for that
            // NEW - Feb 4 2022 -- adding stuff to allow nested components
            // TODO - change this to make it accept normal vue-formulate schema!
            if (ele.component) {
                // if it has 'component' key, then its not a form field - its a layout component
                // for now we will allow for ONE-LEVEL of nesting
                // just copy the entire element straight in, but we'll replace 'children' with a map through this function to deal with all children
                // 'class' will apply here -- but not as a prop on formulate input fields
                // so if you want to wrap formulate fields for custom styling, put them in a div with class as children
                const componentObj = cloneDeep(ele);
                if (ele.children) {
                    const childrenTransformed = ele.children.map(child => this.buildFormElement(child));
                    componentObj.children = childrenTransformed;
                }
                return componentObj;
            } else {
                const label = ele.label || capitalize(ele.name);
                const formField = {
                    type: ele.type || 'text',
                    name: ele.name,  // gotta have this
                    label: label,
                };
                formField['validation-name'] = ele['validation-name'] || label;
                // your validation stuff was kinda messy... try to use 'validation' for everything going forward
                // but you have some older stuff with .required
                // validation can either be pipe-separated string, or array of arrays -- array of arrays is most powerful
                // we will either respect ele.validation OR ele.required if no ele.validation -- can't do both on same element - should be OK for you - just add required to validation if needed
                if (ele.validation) {
                    formField.validation = ele.validation;
                } else if (ele.required) {
                    formField.validation = 'required';
                }
                if (ele.options) {
                    formField.options = ele.options;  // can be array of { value, label } or object with { value: label }
                }
                if (ele.description || ele.help) {
                    formField.help = ele.description || ele.help;
                }
                if (ele.placeholder) {
                    formField.placeholder = ele.placeholder;
                }
                if (ele.hide_if_true) {
                    const fieldToHide = ele.hide_if_true;
                    formField['@input'] = (event) => this.handleHideIfTrue(fieldToHide, event);  // this handler will be called when this checkboxes value changes, payload will be (field_name, event)
                }
                if (ele.attrs) {  // attrs should be a dict/object for extra attributes to add to input element
                    // example - for textarea, you can use attrs: {'rows': 6} in your field definition on backend
                    // pass along other non-prop attributes to the input element
                    Object.assign(formField, ele.attrs);
                }
                if (ele.value) {
                    // ADDED FEB 10 2022 -- use this to set initial value - make sure you pass the correct type
                    formField.value = ele.value;
                }
                // make a list of other props we'll allow on elements, just pass through directly
                const otherAllowedProps = [
                    'outer-class', 'wrapper-class', 'label-class', 'element-class', 'input-class', 'help-class', 'errors-class', 'error-class',
                    // you should also allow [elementKey]-[stateKey]-class - stateKeys are 'hasErrors', 'hasValue', 'isValid' 
                    'debounce', 'disableErrors', 'error', 'errors', 'error-behavior', 'help-position', 'id', 'keep-model-data', 'label-position',
                    'show-errors', 'validation-rules', 'validation-messages', 
                ];
                // loop through all entries in ele, if it has a key in otherAllowedProps set it on formField object
                Object.entries(ele).forEach(([eleKey, eleVal]) => {
                    if (otherAllowedProps.includes(eleKey)) {
                        formField[eleKey] = eleVal;
                    }
                });
                return formField;
            }
        },
        getInitialValues(transObj) {
            // get an object with initial values for all items in the original transition object
            // gets from 'initial' property -- if not there, no initial value is set, which is OK with vue formulate
            // return the object of initial values -- does not set it here
            // will not get any initial values if there are nested items -- only top level!
            const collectDataItems = transObj.collect_data || [];  // should have checked if we had it before calling this anyways
            let initialValues = {};
            collectDataItems.forEach(cd => {
                if (cd.initial && cd.name) {
                    // if you have to coerce anything based on field type you can check cd.type
                    initialValues[cd.name] = cd.initial;
                }
            });
            return initialValues;
        },
        getInitialSchema(transObj) {  // pass in the full transition object
            // build AND RETURN (don't set) the initial form schema (ALL fields)
            // uses whatever was passed in transition object (this.activeTransition)
            // should only call this ONCE when loading the transition object (or when loading a new transition object)
            // but do not call this when rebuilding the schema to update based on hidden fields
            const collectData = transObj.collect_data || [];  // should have checked if we had it before calling this anyways
            // figure out if this is ONLY a confirmation message?
            const isOnlyConfirmation = !!collectData && Array.isArray(collectData) && (collectData.length === 1) && (collectData[0]['name'] === 'confirmation');
            this.collectDataIsOnlyConfirmation = isOnlyConfirmation;
            this.collectDataSubmitLabel = transObj.label;  // set the label for form submit button
            // build the fields first, then add the submit button and cancel button
            // map through all items in the list, deal with any specials, return in format for VueFormulate form schema
            let initialSchema = collectData.map(cd => {
                // HANDLE YOUR 'confirmation' messages first -- to show a confirmation message, with form submit still
                if (cd.name === 'confirmation') {
                    return this.buildConfirmationElement(cd);
                }
                // could add other conditionals here...
                return this.buildFormElement(cd);
                /* const buttonDiv = {
                    component: 'div',
                    class: 'd-flex gap-2',
                    children: [
                        {
                            type: 'submit',
                            label: transObj.label,
                        },
                        {
                            type: 'button',
                            label: 'Cancel',
                            '@click': 'cancel',
                            'outer-class': ['minimal'],
                        },
                    ],
                };
                schema.push(buttonDiv);
                */
            });
            return initialSchema;
        },
        setFinalSchema() {
            // get the initial schema from this.initialSchema, then filter it based on what should be hidden/shown
            // now run it through filters to make the final schema
            let schema = this.initialSchema.filter(fld => {
                // your custom elements may not have 'name' -- if no name, just let them through
                return fld.name ? !this.collectDataHiddenFields.has(fld.name) : true;
            });
            // return schema;  // finally, return the build schema
            // just set it here - for consistency on how you set it
            this.finalSchema = cloneDeep(schema);
        },
        startTransition(transObj) {
            console.log('HELLO FROM startTransition');
            if (!this.canStartTrigger) return;  // if we can't trigger right now, just return 
            // now that we passed that checkpoint... set this.canStartTrigger FALSE to prevent others while this one is running
            this.canStartTrigger = false;
            this.activeTransition = Object.freeze(Object.assign({}, transObj));  // freeze it -- this we can always pull from here -- until we replace it on reset
            // called when user selects a transition -- may need to show modal to collect data
            // this just figures out what is next and calls that
            const hasCollectData = !!transObj.collect_data && Array.isArray(transObj.collect_data) && transObj.collect_data.length;
            if (hasCollectData) {
                console.log('we DO have collectData');
                // if we have to collect data, build the schema, show the modal and gather it - otherwise proceed with calling it
                this.initialSchema = this.getInitialSchema(this.activeTransition);
                this.collectDataValues = this.getInitialValues(this.activeTransition);
                this.setFinalSchema();  // now build the filtered version
                this.$nextTick(() => {
                    // give it a next tick to allow the schema to update before showing form, has to run through finalSchema computed field
                    this.collectDataModal = true;  // show the modal - AFTER doing other stuff above
                })
            } else {
                // no data to collect?  just trigger the transition directly
                // call it async/await?  not here...
                this.callTransition(transObj, null);
            }
        },
        async handleCollectDataSubmit(submitData) {
            // make this async and call it from form @submit -- then FormulateForm will automatically expose 'isLoading' property on the form context object
            console.log('HELLO FROM handleCollectDataSubmit');
            console.log(submitData);  // submitData should be values from collectData
            // call it async/await?  not here...
            await this.callTransition(this.activeTransition, submitData);
        },
        handleCollectDataCancel() {
            // only called from a specific click on the form cancel button
            // NOT emitting cancel in here -- that will happen automatically if modal is closed and transition was not completed
            console.log('HELLO FROM handleCollectDataCancel');
            this.collectDataModal = false;  // close the modal
        },
        handleHideIfTrue(fieldToHide, payload) {
            // this handler will be called if you have a checkbox with .hide_if_true set in the schema from backend
            // when this checkboxes value changes, payload will be (field_name, event)
            const eventValue = payload;  // for vue formulate checkbox, the payload is the value...
            console.log(`HIDE IF TRUE FOR FIELD: ${fieldToHide} - ${eventValue}`);
            // how to change the schema???? just mutate the schema object directly???  add a 'hidden' or 'visible' prop to each item, then just toggle that?
            if (eventValue === true) {
                this.collectDataHiddenFields.add(fieldToHide);
            } else if (eventValue === false) {
                this.collectDataHiddenFields.delete(fieldToHide);
            }
            // now rebuilt the schema with those changes -- make sure your schema builder checks for hidden fields
            this.setFinalSchema();  // builds off of initial schema
        },
        handleLoadingState(loadingState) {
            // helper to handle setting this.loading and emit to parent
            this.loading = loadingState;
            this.$emit('loading', loadingState);
        },
        async callTransition(transObj, collectData) {
            console.log('HELLO FROM callTransition');
            // pass in 2 args -- 1) full transition object and 2) any collectData we gathered, if required
            // do we need to prep collectData for sending??? set defaults?  change types???
            // need to put things into one object as backend is expecting
            const finalCollectData = collectData ? { ...collectData } : {};
            const triggerData = {
                trigger: transObj.trigger,
                ...finalCollectData,
            };
            this.handleLoadingState(true);  // helper to set this.loading AND emit to parent
            let respData = {};  // object we'll pass to handleStateChange() - just a default
            try {
                // const resp = await suppliosApi.rfxResponse.call_transition(this.id, triggerData);
                const resp = await invoke(suppliosApi, this.endpointString, this.localObjectId, triggerData);
                // successful?  emit something to parent to refresh?
                // this.handleStateChange(resp.data);  // should be object with keys 
                // just set respData, then handle at the end
                respData = resp.data || {};
                // { 
                //     success (bool), 
                //     message (string), 
                //     object (serialized object being transitioned, maybe with sparse fields),
                //     redirect_url, (optional, added Oct 4 2022),
                // }
                // don't change what you pass up here unless you do it for ALL things that use this component, because they may have other things that call methods with same signature/params
            } catch (err) {
                console.log('got an error - below');
                console.log(err);
                console.log(typeof err);
                console.log(err.toJSON());
                // err.response is where all the good stuff is:  { config, data, headers, request, status, statusText }
                if (err.response?.status === 403) {  // if we got 403, either we have someone trying to do bad things, or the state just got updated and that transition is no longer available
                    this.$toast('That action is no longer available.  Try refreshing the page, then try again.');
                } else {
                    this.apiErrorHandler(err, 'Error while processing this.  Please contact Supplios support.');
                }
            }
            this.stateChangeComplete = true;  // must do this before closing modal so we know NOT to emit 'cancel' event
            this.collectDataModal = false;
            // originally had handleStateChange in try block, but moving here... will do it before stopping loading state?
            this.handleStateChange(respData);  // this could trigger a redirect if we have redirect_url
            this.handleLoadingState(false);
        },
        handleStateChange(respData) {
            console.log('HELLO FROM handleStateChange - respData:', respData);
            // make sure this can handle getting an empty {} - that means something went wrong with calling transition
            // pass something up to parent, such as new state if we get that back from api after transition?
            // we'll pass up whatever we got back from api -- should be this:
            // { success (bool), message (string), redirect_url (optional), object (serialized object being transitioned, maybe with sparse fields) }
            const { success, message, redirect_url } = respData;  // can get these:  { success, message, object, redirect_url }
            // if we have redirect_url, then we will not show message, just redirect, but AFTER emitting...?
            if (message && !redirect_url) {
                this.showSuccessMessage(message);
            }
            if (success && !redirect_url) {
                // success will be undefined if you got empty object as respData
                // we are only doing this if we do not have redirect_url, in case parent was also going to do a redirect
                // might have to pass some other variable like "force_redirect" or something, to specify which order is correct... but hopefully not
                this.$emit('state-change', respData);  // NOTE - this passes up everything, including redirect_url
            }
            if (redirect_url) {
                window.location.assign(redirect_url);
            }
            // and also refresh the state?  maybe a prop should tell us what to do...
        },
        showSuccessMessage(message) {  // where to get message?
            if (!message) return;
            this.$toast.success(message);
        },
        onModalHide() {
            // check if transition was completed -- if not we must emit 'cancel'
            // because just watching for specific @cancel or @close events does not cover all cases where modal is closed
            // this works as a catch-all
            // DO NOT also emit 'cancel' from other things that close modal, or else you'll be emitting 2 cancel events, might screw up parent
            if (!this.stateChangeComplete) {
                this.$emit('cancel');
            }
        },
        onModalHidden() {
            // reset things on modal hidden -- wait until totally hidden
            this.resetActiveTransition();
            this.canStartTrigger = true;  // don't call this method until you know you're ready to start another transition!  like from @hidden of modal
        },
        /////////////////////////////////////////////////
        /////  FOR FETCHING TRANSITIONS /////////////////
        async fetchTransitions() {  // fetch transitions and stash on transitions
            // first, see if you're using transitions from prop -- if so, bail on this
            if (!this.isFetchMode) return;
            this.loadingTransitions = true;
            try {
                const { data } = await invoke(suppliosApi, this.fetchEndpoint, this.localObjectId);
                this.transitions = data;
                this.$emit('transitions-loaded', this.transitions);  // pass these to parent in case you're using like a renderless component
            } catch (err) {
                this.apiErrorHandler(err, 'Error getting transitions');
            }
            this.loadingTransitions = false;
        },
    }
}
</script>

<style>

</style>